import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Truck, Heart, Package, Users } from "lucide-react";

export default function AdminRewards() {
  const [data, setData] = useState({ customers: [], totals: { customers: 0, orders: 0, shipping_saved: 0, donated: 0 } });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shipping-rewards");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const t = data.totals || {};
  const STATS = [
    { label: "Customers in program", value: t.customers || 0, icon: Users },
    { label: "Total orders", value: t.orders || 0, icon: Package },
    { label: "Shipping discounts given", value: `$${(t.shipping_saved || 0).toFixed(2)}`, icon: Truck },
    { label: "Donated to pet surgeries", value: `$${(t.donated || 0).toFixed(2)}`, icon: Heart },
  ];

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-paw-text dark:text-white">Refill & Shipping Rewards</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-1">Refill shipping: first order ships full, every refill ships 50% off while the prescription is valid. Plus donations per order.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-paw-border dark:border-white/10 px-3 py-2 text-sm font-manrope text-paw-text-secondary dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-7">
        {STATS.map((s) => (
          <div key={s.label} className="bg-white dark:bg-[#13161f] border border-paw-border dark:border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 mb-2"><s.icon className="w-4 h-4" /><span className="font-manrope text-[12px]">{s.label}</span></div>
            <p className="font-outfit text-2xl font-bold text-paw-text dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-[#13161f] border border-paw-border dark:border-white/5 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-paw-primary" /></div>
        ) : (data.customers || []).length === 0 ? (
          <div className="py-16 text-center font-manrope text-sm text-paw-text-muted dark:text-white/40">No paid orders yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-paw-border dark:border-white/5 font-manrope text-[12px] uppercase tracking-wider text-paw-text-muted dark:text-white/40">
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Orders</th>
                  <th className="px-4 py-3 font-medium">Next reward</th>
                  <th className="px-4 py-3 font-medium text-right">Shipping saved</th>
                  <th className="px-4 py-3 font-medium text-right">Donated</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c, i) => (
                  <tr key={i} className="border-b border-paw-border/60 dark:border-white/5 last:border-0 font-manrope text-sm text-paw-text dark:text-white/80">
                    <td className="px-4 py-3">
                      <p className="font-medium">{c.customer || c.email}</p>
                      {c.email && <p className="text-[12px] text-paw-text-muted dark:text-white/40">{c.email}</p>}
                    </td>
                    <td className="px-4 py-3">{c.orders}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-[12px] font-medium ${c.next_reward === "Free shipping" ? "bg-paw-success/10 text-paw-success" : c.next_reward === "50% off shipping" ? "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300" : "bg-paw-warning/10 text-paw-warning"}`}>{c.next_reward}</span>
                    </td>
                    <td className="px-4 py-3 text-right">${(c.shipping_saved || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">${(c.donated || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
